import { useEffect, useState, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { cn } from '@/utils';
import { ReactComponent as Cloud } from '@/assets/image/Cloud.svg';
import { compressImage, shouldCompress } from '@/utils/imageCompression';

interface IconSelectorProps {
  id: string;
  onChange?: (link: Record<string, any> | null) => void;
  value?: Record<string, any> | null;
  options?: Record<string, any>[];
}

const IconSelector: React.FC<IconSelectorProps> = ({ id, onChange, value, options = [] }) => {
  // Create a stable string representation of options
  const optionsString = useMemo(() => JSON.stringify(options), [options]);
  
  const [links, setLinks] = useState<Record<string, any>[]>(options);

  // Use the memoized string for dependency
  useEffect(() => {
    setLinks(options);
  }, [optionsString]);

  const [selectedLink, setSelectedLink] = useState<Record<string, any> | null>(value || null);
  
  // Ref to store the latest onChange function
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Ref to track if we should skip the next onChange call
  const skipNextOnChange = useRef(false);

  useEffect(() => {
    // Skip calling onChange if we just set the value from it
    if (skipNextOnChange.current) {
      skipNextOnChange.current = false;
      return;
    }
    
    if (onChangeRef.current) {
      onChangeRef.current(selectedLink as Record<string, any>);
    }
  }, [selectedLink]);

  const handleLinkClick = (link: Record<string, any> | null) => {
    setSelectedLink(link);
  };

  const handleKeyDown = (e: React.KeyboardEvent, link: Record<string, any>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLinkClick(link);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        let processedFile = file;
        
        // Check if file needs compression
        if (shouldCompress(file) && file.type.startsWith('image/')) {
          try {
            processedFile = await compressImage(file, { maxSizeMB: 1, maxWidth: 512, maxHeight: 512, quality: 0.8 });
          } catch (compressionError) {
            // Using console.error as a better solution for file processing errors

            return;
          }
        } else if (file.size > 2 * 1024 * 1024) {
          // Using console.error as a better solution for file size errors

          return;
        }

        const idx = uuidv4();
        const newLinkObject: Record<string, any> = {
          _id: idx,
          title: processedFile.name,
          url: URL.createObjectURL(processedFile),
          local: true,
          file: processedFile,
        };
        setLinks((prev) => [newLinkObject, ...prev]);
        if (onChangeRef.current) {
          skipNextOnChange.current = true;
          onChangeRef.current(newLinkObject);
        }
        setSelectedLink(newLinkObject);
      } catch (error) {
        // Using console.error as a better solution for general file processing errors

      }
    }
  };

  // Generate a stable key for each link
  const generateKey = (link: Record<string, any> | null, index: number) => {
    if (!link) return `link-null-${index}`;
    if (link._id) return `link-${link._id}`;
    if (link.id) return `link-${link.id}`;
    if (link.url) return `link-${link.url}`;
    // Use index as fallback for stable keys
    return `link-${index}`;
  };

  return (
    <div id={id} className="flex flex-col gap-2">
      {value && <img src={value?.url || value?.localUrl} alt="icon" width={40} height={40} />}

      <div className="media-icons w-full">
        <div className="links-cover max-h-[5.9rem] w-full grid grid-cols-4 sm:grid-cols-8 gap-2 overflow-x-auto scrollbar-hide">
          {/* Default "No Icon" option */}
          <div
            key="no-icon"
            className={cn(
              'flex items-center justify-center h-[2.5rem] cursor-pointer overflow-hidden rounded-lg p-1 hover:bg-primary-50',
              !selectedLink ? 'bg-primary-50' : '',
            )}
            style={
              !selectedLink
                ? {
                    borderRadius: '0.4rem',
                    border: '1px solid #17408F',
                    background: ' #F4F7FE',
                  }
                : {}
            }
            onClick={() => handleLinkClick(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLinkClick(null);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="No icon"
          >
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20ZM15.59 7L12 10.59L8.41 7L7 8.41L10.59 12L7 15.59L8.41 17L12 13.41L15.59 17L17 15.59L13.41 12L17 8.41L15.59 7Z" fill="currentColor"/>
              </svg>
            </div>
          </div>
          
          {links && links.length > 0 && links.map((link, index) => (
            <div
              key={generateKey(link, index)}
              className={cn(
                'flex items-center justify-center h-[2.5rem] cursor-pointer overflow-hidden rounded-lg p-1 hover:bg-primary-50',
                selectedLink?._id === link._id ? 'bg-primary-50' : '',
              )}
              style={
                selectedLink?._id === link._id
                  ? {
                      borderRadius: '0.4rem',
                      border: '1px solid #17408F',
                      background: ' #F4F7FE',
                    }
                  : {}
              }
              onClick={() => handleLinkClick(link)}
              onKeyDown={(e) => handleKeyDown(e, link)}
              role="button"
              tabIndex={0}
              aria-label={`Select ${link.title}`}
            >
              <img src={link.url || link.localUrl} alt={link.title} className="w-6 h-6 object-contain" />
            </div>
          ))}
        </div>
      </div>
      
      {/* Or divider */}
      <div className="flex items-center justify-center py-2">
        <div className="flex-1 border-t border-gray-300"></div>
        <span className="mx-4 text-sm text-gray-500 font-medium">or</span>
        <div className="flex-1 border-t border-gray-300"></div>
      </div>
      
      <div className="upload">
        <input
          type="file"
          id="thumbnail"
          className="hidden"
          accept="image/*"
          onChange={handleFileChange}
        />
        <label
          htmlFor="thumbnail"
          className="flex min-h-[5rem] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-400 bg-[#f5f5f5] box-decoration-slice px-5 py-3"
        >
          <Cloud />
          <p className="tooltip font-inter text-xs font-semibold text-[#475467]">Upload your own Icon</p>
        </label>
      </div>
    </div>
  );
};

export default IconSelector;